import { createFocusTrap } from 'focus-trap'
import iconCancelSvg from '../svg/icon-cancel.svg?raw'

export interface ModalShowOptions {
  initialFocus?: HTMLElement | (() => HTMLElement | null) | null
  bodyScrollLock?: boolean
  lenisPrevent?: boolean
}

/** Single shared modal: one overlay, one dialog. Renders via show(render). Uses classList only for overlay/dialog so --hidden is never overwritten. */
export class Modal {
  private dialog: HTMLDivElement
  private overlay: HTMLDivElement
  private focusTrap: ReturnType<typeof createFocusTrap> | null = null
  private lastFocusedElement: HTMLElement | null = null
  private escapeHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.hide()
  }

  constructor(container: HTMLElement) {
    this.overlay = container.querySelector('.modal__overlay') as HTMLDivElement
    this.dialog = this.overlay?.querySelector('.modal') as HTMLDivElement
    if (!this.overlay || !this.dialog) {
      throw new Error('Modal elements not found: .modal__overlay, .modal')
    }
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.hide()
    })
    this.dialog.addEventListener('keydown', this.escapeHandler)
  }

  /** Replace dialog content without showing (e.g. after reset in settings). */
  updateContent(render: (dialog: HTMLDivElement) => void): void {
    this.dialog.innerHTML = ''
    render(this.dialog)
  }

  /** Render content into dialog, then show. */
  show(render: (dialog: HTMLDivElement) => void, options?: ModalShowOptions): void {
    this.lastFocusedElement = document.activeElement as HTMLElement
    this.dialog.innerHTML = ''
    render(this.dialog)

    const initialFocus =
      options?.initialFocus === undefined || options?.initialFocus === null
        ? this.dialog
        : typeof options.initialFocus === 'function'
          ? options.initialFocus()
          : options.initialFocus

    this.focusTrap = createFocusTrap(this.dialog, {
      escapeDeactivates: true,
      returnFocusOnDeactivate: true,
      initialFocus: initialFocus ?? this.dialog,
      // Allow outside clicks (e.g. overlay) to reach the DOM; we close in overlay listener
      clickOutsideDeactivates: true,
    })

    if (options?.lenisPrevent) this.overlay.setAttribute('data-lenis-prevent', '')
    document.body.style.overflow = 'hidden'

    // Double rAF so the browser paints the hidden state; force reflow so opacity transition runs on fade-in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void this.overlay.offsetHeight
        void this.dialog.offsetHeight
        this.overlay.classList.remove('modal__overlay--hidden')
        this.dialog.classList.remove('modal--hidden')
        this.focusTrap?.activate()
      })
    })
  }

  hide(onComplete?: () => void): void {
    if (this.focusTrap) {
      this.focusTrap.deactivate()
      this.focusTrap = null
    }
    this.overlay.removeAttribute('data-lenis-prevent')
    document.body.style.overflow = ''

    this.overlay.classList.add('modal__overlay--hidden')
    this.dialog.classList.add('modal--hidden')

    setTimeout(() => {
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus()
        this.lastFocusedElement = null
      }
      onComplete?.()
    }, 300)
  }

  getDialog(): HTMLDivElement {
    return this.dialog
  }

  destroy(): void {
    this.dialog.removeEventListener('keydown', this.escapeHandler)
    this.hide()
  }

  // —— Static UI builders (single source of truth for modal DOM) ——

  static createHeader(title: string, onClose: () => void): HTMLDivElement {
    const header = document.createElement('div')
    header.className = 'modal__header'
    const titleEl = document.createElement('h2')
    titleEl.className = 'modal__title'
    titleEl.textContent = title
    header.appendChild(titleEl)
    const closeBtn = document.createElement('button')
    closeBtn.className = 'button button--circle'
    closeBtn.setAttribute('aria-label', 'Zavřít')
    closeBtn.innerHTML = iconCancelSvg
    closeBtn.addEventListener('click', onClose)
    header.appendChild(closeBtn)
    return header
  }

  static createContent(): HTMLDivElement {
    const el = document.createElement('div')
    el.className = 'modal__content'
    return el
  }

  static createSection(title: string, options?: { onReset?: () => void }): HTMLDivElement {
    const section = document.createElement('div')
    section.className = 'modal__section'
    const header = document.createElement('div')
    header.className = 'modal__section-header'
    const titleEl = document.createElement('h3')
    titleEl.className = 'modal__section-title'
    titleEl.textContent = title
    header.appendChild(titleEl)
    if (options?.onReset) {
      const resetBtn = document.createElement('button')
      resetBtn.type = 'button'
      resetBtn.className = 'button button--sm'
      resetBtn.textContent = 'Reset'
      resetBtn.addEventListener('click', options.onReset)
      header.appendChild(resetBtn)
    }
    section.appendChild(header)
    return section
  }

  /** Section with only a title (no border / reset). */
  static createSectionTitleOnly(title: string): HTMLDivElement {
    const section = document.createElement('div')
    section.className = 'modal__section'
    const header = document.createElement('div')
    header.className = 'modal__section-header'
    const titleEl = document.createElement('h3')
    titleEl.className = 'modal__section-title'
    titleEl.textContent = title
    header.appendChild(titleEl)
    section.appendChild(header)
    return section
  }

  /**
   * WebKit/Blink: drives `--slider-fill-pct` for the blue portion left of the thumb.
   * Firefox uses `::-moz-range-progress` and ignores this variable.
   */
  static syncRangeSliderFill(slider: HTMLInputElement): void {
    const min = Number(slider.min)
    const max = Number(slider.max)
    const val = Number(slider.value)
    const lo = Number.isFinite(min) ? min : 0
    const hi = Number.isFinite(max) ? max : 100
    const clamped = Math.min(hi, Math.max(lo, val))
    const pct = hi <= lo ? 0 : ((clamped - lo) / (hi - lo)) * 100
    slider.style.setProperty('--slider-fill-pct', `${pct}%`)
  }

  static createSliderControl(params: {
    label: string
    iconSvg: string
    value: number
    onChange: (value: number) => void
    ariaLabel?: string
  }): HTMLDivElement {
    const control = document.createElement('div')
    control.className = 'modal__control'
    const labelContainer = document.createElement('div')
    labelContainer.className = 'modal__control-label'
    const iconSpan = document.createElement('span')
    iconSpan.className = 'modal__control-icon'
    iconSpan.innerHTML = params.iconSvg
    const labelSpan = document.createElement('span')
    labelSpan.className = 'modal__control-text'
    labelSpan.textContent = params.label
    labelContainer.appendChild(iconSpan)
    labelContainer.appendChild(labelSpan)

    const sliderContainer = document.createElement('div')
    sliderContainer.className = 'modal__slider-container'
    const slider = document.createElement('input')
    slider.type = 'range'
    slider.min = '0'
    slider.max = '100'
    slider.value = String(params.value)
    slider.className = 'modal__slider'
    slider.setAttribute('aria-label', params.ariaLabel ?? `${params.label} hlasitost`)

    Modal.syncRangeSliderFill(slider)

    slider.addEventListener('input', (e) => {
      const el = e.target as HTMLInputElement
      Modal.syncRangeSliderFill(el)
      const v = parseInt(el.value, 10)
      params.onChange(v)
    })

    sliderContainer.appendChild(slider)
    control.appendChild(labelContainer)
    control.appendChild(sliderContainer)
    return control
  }

  static createToggleControl(params: {
    label: string
    iconSvg: string
    checked: boolean
    onChange: (value: boolean) => void
    ariaLabel?: string
  }): HTMLDivElement {
    const control = document.createElement('div')
    control.className = 'modal__control modal__control--inline'
    const labelContainer = document.createElement('div')
    labelContainer.className = 'modal__control-label'
    const iconSpan = document.createElement('span')
    iconSpan.className = 'modal__control-icon'
    iconSpan.innerHTML = params.iconSvg
    const labelSpan = document.createElement('span')
    labelSpan.className = 'modal__control-text'
    labelSpan.textContent = params.label
    labelContainer.appendChild(iconSpan)
    labelContainer.appendChild(labelSpan)

    const toggleContainer = document.createElement('div')
    toggleContainer.className = 'modal__toggle-container'
    const toggle = document.createElement('button')
    toggle.type = 'button'
    toggle.className = 'toggle'
    toggle.setAttribute('aria-label', params.ariaLabel ?? params.label)
    toggle.setAttribute('role', 'switch')
    toggle.setAttribute('aria-checked', String(params.checked))
    toggle.innerHTML = '<span class="toggle__track"><span class="toggle__thumb"></span></span>'
    toggle.addEventListener('click', () => {
      const next = toggle.getAttribute('aria-checked') !== 'true'
      toggle.setAttribute('aria-checked', String(next))
      params.onChange(next)
    })
    toggleContainer.appendChild(toggle)
    control.appendChild(labelContainer)
    control.appendChild(toggleContainer)
    return control
  }

  static createMessage(text: string): HTMLParagraphElement {
    const el = document.createElement('p')
    el.className = 'modal__message'
    el.textContent = text
    return el
  }

  static createQuestion(text: string): HTMLParagraphElement {
    const el = document.createElement('p')
    el.className = 'modal__question'
    el.textContent = text
    return el
  }

  static createButtons(buttons: Array<{ label: string; variant?: 'secondary'; onClick: () => void }>): HTMLDivElement {
    const container = document.createElement('div')
    container.className = 'modal__buttons'
    for (const b of buttons) {
      const btn = document.createElement('button')
      btn.className = b.variant === 'secondary' ? 'button button--secondary' : 'button'
      btn.textContent = b.label
      btn.addEventListener('click', b.onClick)
      container.appendChild(btn)
    }
    return container
  }

  static createSectionWithDescription(title: string, description: string): HTMLDivElement {
    const section = document.createElement('div')
    section.className = 'modal__section'
    const titleEl = document.createElement('h3')
    titleEl.className = 'modal__section-title'
    titleEl.textContent = title
    section.appendChild(titleEl)
    const descEl = document.createElement('p')
    descEl.className = 'modal__description'
    descEl.textContent = description
    section.appendChild(descEl)
    return section
  }

  static createSelectControl(params: { id: string; label: string; options: Array<{ value: string; text: string }> }): {
    container: HTMLDivElement
    select: HTMLSelectElement
  } {
    const container = document.createElement('div')
    container.className = 'modal__control'
    const labelEl = document.createElement('label')
    labelEl.className = 'modal__control-label'
    labelEl.textContent = params.label
    labelEl.setAttribute('for', params.id)
    const select = document.createElement('select')
    select.id = params.id
    select.className = 'modal__select'
    for (const opt of params.options) {
      const option = document.createElement('option')
      option.value = opt.value
      option.textContent = opt.text
      select.appendChild(option)
    }
    container.appendChild(labelEl)
    container.appendChild(select)
    return { container, select }
  }

  static createButton(text: string, onClick: () => void, variant?: 'secondary'): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.className = variant === 'secondary' ? 'button button--secondary' : 'button'
    btn.textContent = text
    btn.addEventListener('click', onClick)
    return btn
  }
}
